import { request } from 'api/request';
import { ACTION_TYPE } from 'api/types';

export type AIBuilderProvider = 'openai' | 'anthropic';
export type AIBuilderMode = 'agent' | 'chat';

export type AIBuilderProps = {
  input: string;
  journeyShortId: string;
  chatId: string;
  files?: File[];
  selectedAction?: {
    id: string;
    type: ACTION_TYPE;
  };
  mode: AIBuilderMode;
};

export type AIBuilderInterruptProps = {
  journeyShortId: string;
  chatId: string;
};

export type QuestionOption = {
  label: string;
  description: string;
};

export type Question = {
  question: string;
  options: QuestionOption[];
  isMultiSelect: boolean;
};

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  text?: string;
  toolName?: string;
  toolCallId?: string;
  toolLabel?: string;
  toolDescription?: string;
  status?: string;
  errorType?: string;
  errorMessage?: string;
  questions?: Question[];
  plan?: PlanData;
};

export type MessagesResponse = {
  chatId: string;
  mode: AIBuilderMode;
  provider: AIBuilderProvider;
  messages: ChatMessage[];
};

export type UpdateSessionModeProps = {
  journeyShortId: string;
  chatId: string;
  mode?: AIBuilderMode;
  provider?: AIBuilderProvider;
};

export type UpdateSessionModeResponse = {
  chatId: string;
  mode: AIBuilderMode;
  provider: AIBuilderProvider;
};

export const runAIBuilder = async (data: AIBuilderProps) => {
  const { input, journeyShortId, chatId, files = [], selectedAction, mode } = data;
  const formData = new FormData();

  formData.append('input', input);
  formData.append('journeyShortId', journeyShortId);
  formData.append('chatId', chatId);
  formData.append('mode', mode);

  if (selectedAction) {
    formData.append('selectedAction', JSON.stringify(selectedAction));
  }

  files.forEach((file) => {
    formData.append('files', file);
  });

  const response = await request.post('v1/ai-builder/run/', formData);
  return response.data;
};

export const loadAIBuilderMessages = async (journeyShortId: string, chatId: string) => {
  const response = await request.get<MessagesResponse>(
    `v1/ai-builder/chat-messages/${journeyShortId}/${chatId}/`
  );
  return response.data;
};

export const updateSessionMode = async (data: UpdateSessionModeProps) => {
  const { journeyShortId, chatId, mode, provider } = data;
  const response = await request.patch<UpdateSessionModeResponse>(
    `v1/ai-builder/chat-messages/${journeyShortId}/${chatId}/`,
    { mode, provider }
  );
  return response.data;
};

export const deleteAIBuilderHistory = async (journeyShortId: string, chatId: string) => {
  const response = await request.delete(`v1/ai-builder/chat-messages/${journeyShortId}/${chatId}/`);
  return response.data;
};

export const interruptAIBuilder = async (data: AIBuilderInterruptProps) => {
  const response = await request.post('v1/ai-builder/interrupt/', data);
  return response.data;
};

export type TranscribeAudioResponse = {
  text: string;
};

export const transcribeAudio = async (audioFile: File) => {
  const formData = new FormData();
  formData.append('audioFile', audioFile);

  const response = await request.post<TranscribeAudioResponse>(
    'v1/ai-builder/transcribe/',
    formData
  );
  return response.data;
};

export type QuestionAnswer = {
  selectedOptions: string[];
  customText: string;
};

export type AnswerQuestionsProps = {
  journeyShortId: string;
  chatId: string;
  answers: QuestionAnswer[];
};

export const answerQuestions = async (data: AnswerQuestionsProps) => {
  const { journeyShortId, chatId, answers } = data;
  const response = await request.post(
    `v1/ai-builder/answer-questions/${journeyShortId}/${chatId}/`,
    { answers }
  );
  return response.data;
};

// Plan types
export type PlanTaskStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

export type TodoItem = {
  id: string;
  content: string;
  status: PlanTaskStatus;
};

export type PlanTask = {
  id: string;
  name: string;
  description: string;
  status: PlanTaskStatus;
};

export type PlanData = {
  header: string;
  shortDescription: string;
  tasks: PlanTask[];
  planDetails: string;
};

export type AcceptPlanProps = {
  journeyShortId: string;
  chatId: string;
};

export type AcceptPlanResponse = {
  status: 'accepted';
  mode: 'agent';
};

export type RejectPlanProps = {
  journeyShortId: string;
  chatId: string;
};

export type RejectPlanResponse = {
  status: 'rejected';
};

export const acceptPlan = async (data: AcceptPlanProps): Promise<AcceptPlanResponse> => {
  const { journeyShortId, chatId } = data;
  const response = await request.post<AcceptPlanResponse>(
    `v1/ai-builder/accept-plan/${journeyShortId}/${chatId}/`
  );
  return response.data;
};

export const rejectPlan = async (data: RejectPlanProps): Promise<RejectPlanResponse> => {
  const { journeyShortId, chatId } = data;
  const response = await request.post<RejectPlanResponse>(
    `v1/ai-builder/reject-plan/${journeyShortId}/${chatId}/`
  );
  return response.data;
};
